import { app_domain } from 'app/config/constants'
import { clsx } from 'clsx'
import { Button } from 'component/Button'
import { Space } from 'component/Space'
import { Typo } from 'component/Typo'
import { Upload } from 'component/Upload'
import { Vertical } from 'component/Vertical'
import { i18n } from 'i18n'
import { Icon } from 'icon/Icon'
import { observer } from 'mobx-react-lite'
import LogoSvg from 'page/FullbodyScreen/asset/logo.svg'
import PersonSvg from 'page/FullbodyScreen/asset/person.svg'
import { QrUploadState, useQrUpload } from 'page/SelfieScreen/useQrUpload'
import { ReactElement } from 'react'
import { store } from 'store/store'
import css from './FullbodyScreen.module.scss'

type BaseProps = {
  state: QrUploadState
  children: [ReactElement, ReactElement]
}

// На своём домене показываем фирменный логотип, на остальных — название приложения.
const Logo = observer(() => {
  if (app_domain === 'dresscode') return <LogoSvg className={css.logo} height={20} />
  return <Typo size={16} semi upper>{store.title}</Typo>
})

// Фигура на всех шагах одна и та же: меняются только цвет и свободная высота.
const Figure = observer(({ state }: { state: QrUploadState }) => {
  const success = state === 'success'
  const error = state === 'error'
  const processing = state === 'processing'
  const className = clsx(css.person, success && css.success, error && css.error)

  return (
    <Space className={css.figure} center>
      <Space className={css.stack}>
        <PersonSvg className={className} />
        {/* Заливка снизу вверх показывает, что загрузка идёт. */}
        {processing && <PersonSvg className={css.fill} />}
      </Space>
      {(success || error) && (
        <Space className={clsx(css.mark, success ? css.markSuccess : css.markError)} center>
          <Icon className={css.markIcon} name={success ? 'check_circle' : 'cancel'} fill />
        </Space>
      )}
    </Space>
  )
})

const Base = observer(({ state, children }: BaseProps) => {
  const [center, bottom] = children
  return <Vertical className={css.scroll} center>
    <Vertical className={css.inner} gap={24} pad={24} center>
      <Space pad={[24, 0]}>
        <Logo />
      </Space>
      <Figure state={state} />
      {center}
      {bottom}
    </Vertical>
  </Vertical>
})

// Страница загрузки фото в полный рост: сюда попадают с телефона по QR со стенда.
export const FullbodyScreen = observer(() => {
  const { state, input, reset, onClose, onFile, onCamera, onCameraFile } = useQrUpload('full')

  if (state === 'processing') return <Base state={state}>
    <Typo size={16} center pre>
      {i18n('UploadingPhoto')}
    </Typo>
    <Button height={48} upper large flat wide onClick={reset}>
      {i18n('Cancel')}
    </Button>
  </Base>

  if (state === 'success') return <Base state={state}>
    <Typo size={16} center pre balance>
      {i18n('PhotoSuccessfullyUploadedContinueTryingOnStand')}
    </Typo>
    <Button height={48} upper large flat wide onClick={onClose}>
      {i18n('Close')}
    </Button>
  </Base>

  if (state === 'error') return <Base state={state}>
    <Typo size={16} center pre>
      {i18n('SomethingWentWrongPleaseTryAgain')}
    </Typo>
    <Button height={48} upper large flat wide onClick={reset}>
      {i18n('GoBack')}
    </Button>
  </Base>

  return <Base state={state}>
    <Vertical gap={12} wide>
      <Typo size={13} medium upper center>
        {i18n('UploadFullbodyPhoto')}
      </Typo>
      <Typo size={13} pre center>
        {i18n('ShowYourselfFullyForFullbodyPhoto')}
      </Typo>
    </Vertical>
    <Vertical gap={12} wide>
      <Space>
        <Button height={48} upper large primary wide onClick={onCamera}>
          {i18n('OpenCamera')}
        </Button>
      </Space>
      <Upload wide accept="image" onFile={onFile}>
        <Button height={48} upper large flat wide>
          {i18n('LoadFromStorage')}
        </Button>
      </Upload>
      <input
        ref={input}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={onCameraFile}
        style={{ display: 'none' }}
      />
    </Vertical>
  </Base>
})
